import { useMemo } from 'react';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Building2,
  Eye,
  Mail,
  Phone,
  RefreshCw,
  Search,
  User
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { cn } from '../../lib/utils';
import { Contact, ContactWithDetails, ContactSortKey, ContactFilter } from '../../types';
import { ContactDetailDialog } from '../contacts/ContactDetailDialog';

interface ContactsPageProps {
  // State
  contacts: Contact[];
  loading: boolean;
  selectedContact: ContactWithDetails | null;
  detailsOpen: boolean;
  setDetailsOpen: (open: boolean) => void;
  detailsLoading: boolean;
  search: string;
  setSearch: (search: string) => void;
  filter: ContactFilter;
  setFilter: (filter: ContactFilter) => void;
  sort: { column: ContactSortKey; direction: 'asc' | 'desc' };
  toggleSort: (column: ContactSortKey) => void;
  totalElements: number;

  // Actions
  fetchContacts: () => Promise<{ success: boolean; error?: string }>;
  openDetails: (contact: Contact) => void;
}

export function ContactsPage({
  contacts,
  loading,
  selectedContact,
  detailsOpen,
  setDetailsOpen,
  detailsLoading,
  search,
  setSearch,
  filter,
  setFilter,
  sort,
  toggleSort,
  totalElements,
  fetchContacts,
  openDetails
}: ContactsPageProps) {
  // Stats
  const stats = useMemo(() => {
    const qualified = contacts.filter((c) => c.avgBantScore >= 3).length;
    const withOpportunities = contacts.filter((c) => c.totalOpportunities > 0).length;
    const totalValue = contacts.reduce((sum, c) => sum + (c.totalOpportunityValue || 0), 0);
    return { qualified, withOpportunities, totalValue };
  }, [contacts]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getBantScoreBadge = (score: number) => {
    if (score >= 3) {
      return <Badge variant="approved">{score.toFixed(1)}</Badge>;
    } else if (score >= 2) {
      return <Badge variant="pending">{score.toFixed(1)}</Badge>;
    } else if (score > 0) {
      return <Badge variant="rejected">{score.toFixed(1)}</Badge>;
    }
    return <Badge variant="secondary">-</Badge>;
  };

  const SortIcon = ({ column }: { column: ContactSortKey }) => {
    if (sort.column !== column) {
      return <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />;
    }
    return sort.direction === 'asc' ? (
      <ArrowUp className="ml-1 h-3 w-3" />
    ) : (
      <ArrowDown className="ml-1 h-3 w-3" />
    );
  };

  return (
    <>
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span>CRM</span>
        <span>/</span>
        <span className="font-medium text-foreground">Contatos</span>
      </div>

      <Card className="shadow-sm">
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <CardTitle>Contatos</CardTitle>
            <CardDescription>
              Visualize e gerencie os contatos e suas oportunidades.
            </CardDescription>
          </div>

          <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar por nome, telefone..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="pl-9"
              />
            </div>
            <Button type="button" onClick={fetchContacts} disabled={loading}>
              <RefreshCw className={cn('mr-2 h-4 w-4', loading && 'animate-spin')} />
              <span className="hidden md:inline">{loading ? 'Atualizando' : 'Recarregar'}</span>
              <span className="md:hidden">Atualizar</span>
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Stats Summary */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm border-b pb-3">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Total:</span>
              <span className="font-semibold">{totalElements}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-green-600">Qualificados:</span>
              <span className="font-medium text-green-600">{stats.qualified}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-blue-600">Com Oportunidades:</span>
              <span className="font-medium text-blue-600">{stats.withOpportunities}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Valor Total:</span>
              <span className="font-medium">{formatCurrency(stats.totalValue)}</span>
            </div>
          </div>

          <Tabs value={filter} onValueChange={(value) => setFilter(value as ContactFilter)}>
            <TabsList>
              <TabsTrigger value="all">Todos</TabsTrigger>
              <TabsTrigger value="qualified">Qualificados</TabsTrigger>
              <TabsTrigger value="with_opportunities">Com Oportunidades</TabsTrigger>
            </TabsList>

            <TabsContent value={filter}>
              {/* Mobile Card View */}
              <div className="md:hidden space-y-3">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="h-6 w-6 animate-spin" />
                  </div>
                ) : contacts.length === 0 ? (
                  <p className="text-center text-sm text-muted-foreground py-8">
                    Nenhum contato encontrado.
                  </p>
                ) : (
                  contacts.map((contact) => (
                    <Card key={contact.id} className="shadow-sm overflow-hidden">
                      <CardContent className="p-4 space-y-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold break-words flex items-center gap-2">
                              <User className="h-4 w-4 text-muted-foreground" />
                              {contact.name || 'Sem nome'}
                            </div>
                            <div className="text-sm text-muted-foreground flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {contact.phoneNumber}
                            </div>
                          </div>
                          {getBantScoreBadge(contact.avgBantScore)}
                        </div>

                        {contact.company && (
                          <div className="text-sm text-muted-foreground flex items-center gap-1">
                            <Building2 className="h-3 w-3" />
                            {contact.company}
                          </div>
                        )}

                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <div className="text-xs text-muted-foreground">Oportunidades</div>
                            <div className="font-semibold">{contact.totalOpportunities}</div>
                          </div>
                          <div>
                            <div className="text-xs text-muted-foreground">Valor Total</div>
                            <div className="font-semibold">
                              {formatCurrency(contact.totalOpportunityValue)}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center justify-between pt-2 border-t">
                          <div className="text-xs text-muted-foreground">
                            {formatDate(contact.lastOpportunityAt || contact.createdAt)}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openDetails(contact)}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Ver
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead
                        className="cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => toggleSort('name')}
                      >
                        <div className="flex items-center">
                          Nome
                          <SortIcon column="name" />
                        </div>
                      </TableHead>
                      <TableHead
                        className="cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => toggleSort('phone')}
                      >
                        <div className="flex items-center">
                          Telefone
                          <SortIcon column="phone" />
                        </div>
                      </TableHead>
                      <TableHead>Empresa</TableHead>
                      <TableHead
                        className="text-center cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => toggleSort('totalOpportunities')}
                      >
                        <div className="flex items-center justify-center">
                          Oport.
                          <SortIcon column="totalOpportunities" />
                        </div>
                      </TableHead>
                      <TableHead className="text-right">Valor Total</TableHead>
                      <TableHead
                        className="text-center cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => toggleSort('avgBantScore')}
                      >
                        <div className="flex items-center justify-center">
                          BANT
                          <SortIcon column="avgBantScore" />
                        </div>
                      </TableHead>
                      <TableHead
                        className="cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => toggleSort('lastOpportunityAt')}
                      >
                        <div className="flex items-center">
                          Ultima Ativ.
                          <SortIcon column="lastOpportunityAt" />
                        </div>
                      </TableHead>
                      <TableHead className="text-right">Acoes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center">
                          <RefreshCw className="h-6 w-6 animate-spin mx-auto" />
                        </TableCell>
                      </TableRow>
                    ) : contacts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-sm">
                          Nenhum contato encontrado.
                        </TableCell>
                      </TableRow>
                    ) : (
                      contacts.map((contact) => (
                        <TableRow
                          key={contact.id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => openDetails(contact)}
                        >
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4 text-muted-foreground" />
                              {contact.name || '-'}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <Phone className="h-3 w-3" />
                              {contact.phoneNumber}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {contact.company || '-'}
                          </TableCell>
                          <TableCell className="text-center font-semibold">
                            {contact.totalOpportunities}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(contact.totalOpportunityValue)}
                          </TableCell>
                          <TableCell className="text-center">
                            {getBantScoreBadge(contact.avgBantScore)}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {formatDate(contact.lastOpportunityAt)}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                openDetails(contact);
                              }}
                              title="Ver detalhes"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <ContactDetailDialog
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        contact={selectedContact}
        loading={detailsLoading}
      />
    </>
  );
}
